// IE Hub - API Services Index
// Centralized export for all API service classes

// Import classes locally so they exist in this module scope for ApiServices object
import { AuthService } from "./authService";
import { BudgetService } from "./budgetService";
import { DashboardService } from "./dashboardService";
import { FormService } from "./formService";
import { UserService } from "./userService";
import { TermsService } from "./termsService";
import { OnboardingService } from "./onboardingService";
import { LenderService } from "./lenderService";
import { ProfileService } from "./profileService";
import { SupportService } from "./supportService";
import { DependentsService } from "./dependentsService";
import { MarketingService } from "./marketingService";
import { DebtsService } from "./debtsService";
import { DebtCompaniesService } from "./DebtCompaniesService";
import { BenefitsService } from "./benefitsService";
import { HelpSupportService } from "./helpSupportService";
import { UuEvidenceService } from "./uuEvidenceService";

// Re-export for external consumers
export { AuthService } from "./authService";
export { BudgetService } from "./budgetService";
export { DashboardService } from "./dashboardService";
export { FormService } from "./formService";
export { UserService } from "./userService";
export { TermsService } from "./termsService";
export { OnboardingService } from "./onboardingService";
export { LenderService } from "./lenderService";
export { ProfileService } from "./profileService";
export { DocumentsPageService } from "./documentsPageService";
export { SupportService } from "./supportService";
export { DependentsService } from "./dependentsService";
export { MarketingService } from "./marketingService";
export { DebtsService } from "./debtsService";
export { DebtCompaniesService } from "./DebtCompaniesService";
export { BenefitsService } from "./benefitsService";
export { HelpSupportService } from "./helpSupportService";
export { UuEvidenceService } from "./uuEvidenceService";

// Re-export types for convenience
export type {
  ApiResponse,
  ServiceResponse,
  ServiceListResponse,
  RequestConfig,

  // Auth types
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,

  // User types
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UserSearchParams,

  // Form types
  Schema,
  FormSubmitRequest,
  FormSubmitResponse,
  FormValidateRequest,
  FormValidateResponse,

  // Budget types
  IncomeDataRecord,
  IncomeDataResponse,
  GuidanceNoteRecord,
  GuidanceNotesResponse,
  IncomeTriggerRecord,
  IncomeTriggersResponse,
  ButtonActionLogRequest,
  ButtonActionLogRecord,
  ButtonActionLogResponse,
  IncomeUpdateRequest,
  IncomeUpdateResponse,
  CustomerDebtRecord,
  DebtUpsertRequest,
  DebtResponse,
  DebtListResponse,
  PaymentOfferRecord,
  PaymentOfferUpsertRequest,
  PaymentOfferResponse,
  FreePostDataResponse,
  FreePostSharingPayload,
  CustomerSharingResponse,
  CustomerSharingUpsertRequest,
  DebtCategoryKey,
  DebtRepaymentFrequencyUi,
  DebtFormEntry,
  DebtsSchemaState,

  // Dashboard types
  DashboardSummaryDto,
  DashboardDataResponse,
  ProgressSection,
  DashboardButtonActionRequest,
  CustomerProfileData,
  ShareHistoryRecord,
  MarketingServiceData,
  CustomerSupportData,
  SurveyData,

  // Budget Breakdown types
  BudgetBreakdownKPI,
  BudgetInsight,
  ChartData,

  // Common types
  PaginationParams,
  PaginatedResponse,
} from "../types";

/**
 * API Services Collection
 * Provides a unified interface to all API services
 */
export const ApiServices = {
  auth: AuthService,
  budget: BudgetService,
  dashboard: DashboardService,
  form: FormService,
  user: UserService,
  terms: TermsService,
  onboarding: OnboardingService,
  lender: LenderService,
  profile: ProfileService,
  support: SupportService,
  dependents: DependentsService,
  marketing: MarketingService,
  debts: DebtsService,
  debtCompanies: DebtCompaniesService,
  benefits: BenefitsService,
  helpSupport: HelpSupportService,
  uuEvidence: UuEvidenceService,
} as const;

export default ApiServices;
